import { useNow } from "./useNow";
import { useServices } from "@hq/client-react";
import type { Priority, Project, Task } from "@hq/contracts";
import {
  addDays,
  calendarDateOf,
  instantAt,
  normalizeTags,
  type NowContext,
  todayOf,
  wallClockOf,
} from "@hq/core";
import { Button, Calendar, DropdownMenu, toast, Input, Label, Popover, TagInput } from "@z/ui";
import {
  CalendarClock,
  CheckCircle2,
  Pencil,
  Flag,
  Folder,
  FolderInput,
  Inbox,
  Moon,
  Sunrise,
  Tag,
  Trash2,
} from "lucide-react";
import { type ReactElement, type RefObject, useState } from "react";
import { formatDuration, isIsoDate } from "./format";
import { normalizeTag, useAllTags } from "./tags";

export const PRIORITY_LABEL: Record<Priority, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};
export const PRIORITY_CLASS: Record<Priority, string> = {
  high: "text-danger",
  medium: "text-warning",
  low: "text-accent",
};
const PRIORITY_ORDER: readonly Priority[] = ["high", "medium", "low"];

type Controlled = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Non-interactive anchor used when an overflow menu opens this picker. */
  anchorOnly?: boolean;
  returnFocus?: RefObject<HTMLElement | null>;
};

/** The controlled/uncontrolled `open` pair shared by the popovers so a list can open one by key. */
function useOpenState(props: Controlled): [boolean, (open: boolean) => void] {
  const [local, setLocal] = useState(false);
  const open = props.open ?? local;
  const setOpen = (next: boolean) => {
    setLocal(next);
    props.onOpenChange?.(next);
  };
  return [open, setOpen];
}

export interface DueDatePopoverProps extends Controlled {
  task: Task;
  now: NowContext;
  /** The element that opens it; must accept a ref and event props. */
  trigger: ReactElement;
}

/** Deadline picker: a Calendar plus Today / Tomorrow / Clear. Writes through `tasks.setDueDate`. */
export function DueDatePopover({ task, now, trigger, ...controlled }: DueDatePopoverProps) {
  const services = useServices();
  const [open, setOpen] = useOpenState(controlled);
  const today = todayOf(now);
  const set = (date: string | null) => {
    void services.tasks.setDueDate(task.id, date);
    setOpen(false);
  };
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger render={trigger} />
      <Popover.Content align="start" className="w-auto p-0" aria-label="Deadline">
        <Calendar
          today={today}
          onChange={set}
          {...(task.dueDate !== undefined ? { value: task.dueDate } : {})}
        />
        <div className="flex items-center gap-1 border-t border-border p-2">
          <Button variant="ghost" size="sm" onClick={() => set(today)}>
            Today
          </Button>
          <Button variant="ghost" size="sm" onClick={() => set(addDays(today, 1))}>
            Tomorrow
          </Button>
          {task.dueDate !== undefined && (
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => set(null)}>
              Clear
            </Button>
          )}
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

export interface PriorityMenuProps extends Controlled {
  task: Task;
  trigger: ReactElement;
}

export function PriorityMenu({
  task,
  trigger,
  anchorOnly = false,
  returnFocus,
  ...controlled
}: PriorityMenuProps) {
  const services = useServices();
  const [open, setOpen] = useOpenState(controlled);
  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger nativeButton={!anchorOnly} render={trigger} />
      <DropdownMenu.Content finalFocus={returnFocus} aria-label="Priority">
        {PRIORITY_ORDER.map((p, i) => (
          <DropdownMenu.Item
            key={p}
            onClick={() => void services.tasks.setPriority(task.id, p)}
            data-active={task.priority === p ? "" : undefined}
            className="data-active:font-medium"
          >
            <Flag className={PRIORITY_CLASS[p]} />
            {PRIORITY_LABEL[p]}
            <DropdownMenu.Shortcut keys={String(i + 1)} />
          </DropdownMenu.Item>
        ))}
        <DropdownMenu.Separator />
        <DropdownMenu.Item onClick={() => void services.tasks.setPriority(task.id, null)}>
          <Flag />
          No priority
          <DropdownMenu.Shortcut keys="0" />
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export interface SchedulePopoverProps extends Controlled {
  task: Task;
  now: NowContext;
  trigger: ReactElement;
}

/** "Scheduled": a date plus a wall-clock time in the device zone, stored as an instant. */
export function SchedulePopover({
  task,
  now,
  trigger,
  anchorOnly = false,
  returnFocus,
  ...controlled
}: SchedulePopoverProps) {
  const [open, setOpen] = useOpenState(controlled);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger nativeButton={!anchorOnly} render={trigger} />
      <Popover.Content finalFocus={returnFocus} align="start" aria-label="Scheduled">
        <ScheduleForm task={task} now={now} onDone={() => setOpen(false)} />
      </Popover.Content>
    </Popover.Root>
  );
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Mounted only while the popover is open, so its draft starts from the task each time. */
function ScheduleForm({ task, now, onDone }: { task: Task; now: NowContext; onDone: () => void }) {
  const services = useServices();
  const initialDate =
    task.scheduledAt !== undefined ? calendarDateOf(task.scheduledAt, now.timeZone) : todayOf(now);
  const initialTime =
    task.scheduledAt !== undefined
      ? (() => {
          const { hour, minute } = wallClockOf(task.scheduledAt, now.timeZone);
          return `${pad(hour)}:${pad(minute)}`;
        })()
      : "09:00";
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);
  const [duration, setDuration] = useState(
    task.scheduledDurationMinutes !== undefined ? String(task.scheduledDurationMinutes) : "",
  );
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(time);
  const valid = isIsoDate(date) && timeMatch !== null;

  const save = () => {
    if (!timeMatch || !isIsoDate(date)) return;
    const instant = instantAt(date, Number(timeMatch[1]), Number(timeMatch[2]), now.timeZone);
    const minutes = Number.parseInt(duration, 10);
    void services.tasks.schedule(
      task.id,
      instant,
      Number.isFinite(minutes) && minutes > 0 ? minutes : null,
    );
    onDone();
  };
  const clear = () => {
    void services.tasks.schedule(task.id, null);
    onDone();
  };

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <Popover.Title>Scheduled</Popover.Title>
      <div className="grid grid-cols-2 gap-2">
        <Label className="flex-col items-start gap-1 text-xs text-fg-muted">
          Date
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </Label>
        <Label className="flex-col items-start gap-1 text-xs text-fg-muted">
          Time
          <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} required />
        </Label>
      </div>
      <Label className="flex-col items-start gap-1 text-xs text-fg-muted">
        Duration (minutes)
        <Input
          type="number"
          min={5}
          step={5}
          inputMode="numeric"
          placeholder="e.g. 30"
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
        />
      </Label>
      <div className="flex items-center gap-1 pt-1">
        <Button type="submit" size="sm" disabled={!valid}>
          Schedule
        </Button>
        {task.scheduledAt !== undefined && (
          <Button type="button" variant="ghost" size="sm" className="ml-auto" onClick={clear}>
            Clear
          </Button>
        )}
      </div>
      {task.scheduledDurationMinutes !== undefined && (
        <p className="text-xs text-fg-faint">
          Currently {formatDuration(task.scheduledDurationMinutes)}
        </p>
      )}
    </form>
  );
}

export interface ProjectMenuProps extends Controlled {
  task: Task;
  projects: readonly Project[];
  trigger: ReactElement;
}

export function ProjectMenu({
  task,
  projects,
  trigger,
  anchorOnly = false,
  returnFocus,
  ...controlled
}: ProjectMenuProps) {
  const services = useServices();
  const [open, setOpen] = useOpenState(controlled);
  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger nativeButton={!anchorOnly} render={trigger} />
      <DropdownMenu.Content finalFocus={returnFocus} aria-label="Move to project">
        <DropdownMenu.Item
          onClick={() => void services.tasks.moveToProject(task.id, null)}
          data-active={task.projectId === undefined ? "" : undefined}
          className="data-active:font-medium"
        >
          <Inbox />
          Inbox
        </DropdownMenu.Item>
        {projects.length > 0 && <DropdownMenu.Separator />}
        {projects.map((p) => (
          <DropdownMenu.Item
            key={p.id}
            onClick={() => void services.tasks.moveToProject(task.id, p.id)}
            data-active={task.projectId === p.id ? "" : undefined}
            className="data-active:font-medium"
          >
            <Folder />
            {p.name}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export interface TagsPopoverProps extends Controlled {
  task: Task;
  trigger: ReactElement;
}

/**
 * Tag editor: a `TagInput` whose suggestions are every tag in use. Each chip added or removed
 * writes through `tasks.setTags` at once; typing never writes. The form mounts only while
 * open, so the all-tasks query behind the suggestions runs for one row at a time.
 */
export function TagsPopover({
  task,
  trigger,
  anchorOnly = false,
  returnFocus,
  ...controlled
}: TagsPopoverProps) {
  const [open, setOpen] = useOpenState(controlled);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger nativeButton={!anchorOnly} render={trigger} />
      <Popover.Content
        finalFocus={returnFocus}
        align="start"
        aria-label="Tags"
        className="w-64 p-2"
      >
        <TagsForm task={task} />
      </Popover.Content>
    </Popover.Root>
  );
}

function TagsForm({ task }: { task: Task }) {
  const services = useServices();
  const all = useAllTags();
  return (
    <TagInput
      aria-label="Tags"
      autoFocus
      size="sm"
      placeholder="Add a tag…"
      value={task.tags ?? []}
      suggestions={all.map((t) => t.tag)}
      normalize={normalizeTag}
      onChange={(next) => void services.tasks.setTags(task.id, normalizeTags(next))}
    />
  );
}

export interface MoreMenuProps extends Controlled {
  task: Task;
  trigger: ReactElement;
  onDeleted?: (() => void) | undefined;
  onRename?: (() => void) | undefined;
  /** Shows "Edit tags…"; the caller opens its tag editor. */
  onEditTags?: (() => void) | undefined;
  onSchedule?: (() => void) | undefined;
  onPriority?: (() => void) | undefined;
  onMove?: (() => void) | undefined;
  keepFocus?: boolean;
}

export function MoreMenu({
  task,
  trigger,
  onDeleted,
  onRename,
  onEditTags,
  onSchedule,
  onPriority,
  onMove,
  keepFocus = false,
  ...controlled
}: MoreMenuProps) {
  const [open, setOpen] = useOpenState(controlled);
  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger render={trigger} />
      <DropdownMenu.Content align="end" aria-label="More" finalFocus={!keepFocus}>
        <TaskActionItems
          task={task}
          onDeleted={onDeleted}
          onRename={onRename}
          onEditTags={onEditTags}
          onSchedule={onSchedule}
          onPriority={onPriority}
          onMove={onMove}
        />
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export function TaskActionItems({
  task,
  onDeleted,
  onRename,
  onEditTags,
  onSchedule,
  onPriority,
  onMove,
}: Pick<
  MoreMenuProps,
  "task" | "onDeleted" | "onRename" | "onEditTags" | "onSchedule" | "onPriority" | "onMove"
>) {
  const services = useServices();
  const someday = task.status === "someday";
  const now = useNow();
  return (
    <>
      <DropdownMenu.Item onClick={() => void services.tasks.toggleComplete(task.id)}>
        <CheckCircle2 />
        {task.status === "completed" ? "Reopen task" : "Complete task"}
      </DropdownMenu.Item>
      {onRename && (
        <DropdownMenu.Item onClick={onRename}>
          <Pencil />
          Rename
        </DropdownMenu.Item>
      )}
      <DropdownMenu.Separator />
      <DropdownMenu.Item onClick={() => void services.tasks.plan(task.id, todayOf(now))}>
        <Sunrise />
        Plan for today
      </DropdownMenu.Item>
      <DropdownMenu.Item
        onClick={() => void services.tasks.plan(task.id, addDays(todayOf(now), 1))}
      >
        Plan for tomorrow
      </DropdownMenu.Item>
      <DropdownMenu.Item onClick={() => void services.tasks.plan(task.id, null)}>
        Move to Anytime
      </DropdownMenu.Item>
      {onSchedule && (
        <DropdownMenu.Item onClick={onSchedule}>
          <CalendarClock />
          Schedule…
        </DropdownMenu.Item>
      )}
      {onPriority && (
        <DropdownMenu.Item onClick={onPriority}>
          <Flag />
          Priority…
        </DropdownMenu.Item>
      )}
      {onMove && (
        <DropdownMenu.Item onClick={onMove}>
          <FolderInput />
          Move to project…
        </DropdownMenu.Item>
      )}
      {onEditTags && (
        <>
          <DropdownMenu.Item onClick={onEditTags}>
            <Tag />
            Edit tags…
            <DropdownMenu.Shortcut keys="t" />
          </DropdownMenu.Item>
          <DropdownMenu.Separator />
        </>
      )}
      <DropdownMenu.CheckboxItem
        checked={someday}
        onCheckedChange={(checked) =>
          void services.tasks.setStatus(task.id, checked ? "someday" : "open")
        }
      >
        <Sunrise />
        Someday
      </DropdownMenu.CheckboxItem>
      <DropdownMenu.CheckboxItem
        checked={!!task.evening}
        onCheckedChange={(checked) => {
          if (checked)
            void services.tasks
              .plan(task.id, todayOf(now))
              .then(() => services.tasks.setEvening(task.id, true));
          else void services.tasks.setEvening(task.id, false);
        }}
      >
        <Moon />
        This evening
      </DropdownMenu.CheckboxItem>
      <DropdownMenu.Separator />
      <DropdownMenu.Item
        variant="destructive"
        onClick={() => {
          void services.tasks
            .delete(task.id)
            .then(() => {
              onDeleted?.();
              toast({
                title: "Task moved to Trash",
                action: { label: "Undo", onClick: () => void services.tasks.restore(task.id) },
              });
            })
            .catch(() => toast({ title: "Could not delete task", variant: "danger" }));
        }}
      >
        <Trash2 />
        Delete
        <DropdownMenu.Shortcut keys="backspace" />
      </DropdownMenu.Item>
    </>
  );
}
